import { buildPageTree } from '@featit/shared/page-tree';
import { listsPageItemChildren } from '@featit/shared/page-scope-item';
import {
  DASHBOARD_BLOCK_TYPES,
  type IDashboardBlock,
  type IDashboardNewItemConfig,
  type IDashboardWorkItemListConfig,
  type IWorkAreaDashboardPage,
} from '@featit/shared/types/index';
import { parseAddress } from '@featit/shared/addressing/paths';
import { isGuestRoute } from '@/routing/workAreaRouteState';
import type { IPhoneChromeRows } from '@/shell/phoneChromeCore';

/**
 * What a guest's work-area home opens (F-0621, AGM-41). A guest is never shown Home — it is the
 * members' page, and the server leaves it out of a guest's pages — so their home is the first page
 * shared with them, in the order the sidebar draws the tree (P-4: the server's order).
 *
 * - `wait`: the pages have not been answered yet.
 * - `page`: open this page.
 * - `nothing`: no page is shared with them here, or the read was refused, which is how the server
 *   answers a guest holding a role and no page share. The home says so and leads to *Shared with
 *   you*, never to a page that would only refuse them.
 */
export type GuestLandingType =
  | { kind: 'wait' }
  | { kind: 'page'; pageId: string }
  | { kind: 'nothing' };

export function guestLanding({
  pages,
  settled,
  listsHold = false,
}: {
  pages: readonly IWorkAreaDashboardPage[];
  /** Whether the pages' read has answered, a refusal included. */
  settled: boolean;
  /**
   * PX-42: whether the lists of `guestLandingLists` hold something for her; `null` while they read.
   * A home with no such lists ignores it.
   */
  listsHold?: boolean | null;
}): GuestLandingType {
  const first = buildPageTree(pages)[0]?.page;
  if (!first) return settled ? { kind: 'nothing' } : { kind: 'wait' };
  const lists = guestLandingLists(pages);
  if (!lists) return { kind: 'page', pageId: first.id };
  if (listsHold === null) return { kind: 'wait' };
  return { kind: 'page', pageId: listsHold ? lists.page.id : first.id };
}

const formTypesOf = (page: IWorkAreaDashboardPage): string[] =>
  (page.blocks ?? []).flatMap((block) => {
    if (block.type !== DASHBOARD_BLOCK_TYPES.NEW_ITEM) return [];
    const key = (block.config as IDashboardNewItemConfig | undefined)?.itemTypeKey;
    return key ? [key] : [];
  });

/** A page's lists of the work area's items of `itemTypeKey` (never an item's children, AGM-42). */
const listsOf = (page: IWorkAreaDashboardPage, itemTypeKey: string): IDashboardBlock[] =>
  (page.blocks ?? []).filter((block) => {
    if (block.type !== DASHBOARD_BLOCK_TYPES.WORK_ITEM_LIST) return false;
    const config = block.config as IDashboardWorkItemListConfig | undefined;
    return config?.itemTypeKey === itemTypeKey && !listsPageItemChildren(config);
  });

/**
 * PX-42 (I-2405): a guest's form and the list of what she sent through it may be two pages. The page
 * that lists what `page`'s form files: the first other page of hers, in the sidebar's order, with a
 * list of that item type, and those lists. Null when `page` has no form, or no page lists its type.
 * Derived from the pages alone, so any package or hand-made pair of pages is read the same way.
 */
export function guestListingPageOf(
  pages: readonly IWorkAreaDashboardPage[],
  page: IWorkAreaDashboardPage,
): { page: IWorkAreaDashboardPage; lists: IDashboardBlock[] } | null {
  const types = formTypesOf(page);
  for (const { page: other } of flatPageTree(pages)) {
    if (other.id === page.id || other.scopeItemTypeKey) continue;
    const lists = types.flatMap((type) => listsOf(other, type));
    if (lists.length > 0) return { page: other, lists };
  }
  return null;
}

/**
 * PX-42: the page with the form for what `page` lists, when `page` lists a type it has no form for:
 * the first other page of hers with a New item form of that type. Null otherwise.
 */
export function guestFilingPageOf(
  pages: readonly IWorkAreaDashboardPage[],
  page: IWorkAreaDashboardPage,
): IWorkAreaDashboardPage | null {
  const own = formTypesOf(page);
  const listed = (page.blocks ?? []).flatMap((block) => {
    if (block.type !== DASHBOARD_BLOCK_TYPES.WORK_ITEM_LIST) return [];
    const key = (block.config as IDashboardWorkItemListConfig | undefined)?.itemTypeKey;
    return key && !own.includes(key) ? [key] : [];
  });
  if (listed.length === 0) return null;
  for (const { page: other } of flatPageTree(pages)) {
    if (other.id === page.id || other.scopeItemTypeKey) continue;
    if (formTypesOf(other).some((type) => listed.includes(type))) return other;
  }
  return null;
}

/**
 * PX-42: where a guest lands depends on what she has sent. When her first page is a form and another
 * page lists what it files, she lands on the list while it holds something, else on the form; these
 * are the lists that decide it. Null when her first page is no such form.
 */
export function guestLandingLists(
  pages: readonly IWorkAreaDashboardPage[],
): { page: IWorkAreaDashboardPage; lists: IDashboardBlock[] } | null {
  const first = buildPageTree(pages)[0]?.page;
  return first ? guestListingPageOf(pages, first) : null;
}

function flatPageTree(
  pages: readonly IWorkAreaDashboardPage[],
): Array<{ page: IWorkAreaDashboardPage }> {
  const out: Array<{ page: IWorkAreaDashboardPage }> = [];
  const walk = (nodes: ReturnType<typeof buildPageTree<IWorkAreaDashboardPage>>) => {
    for (const node of nodes) {
      out.push({ page: node.page });
      walk(node.children);
    }
  };
  walk(buildPageTree(pages));
  return out;
}

/**
 * AGM-51 (7): on the phone, a guest's landing page is a root. As a page under a work area it is a
 * pushed screen, whose Back goes to Work items — a members' route that takes the guest home, which
 * is this same page. So it draws its title row in place of the back row. `landing` is the page's
 * title when the screen is the guest's landing page, else `null` and the rows are left as they are.
 *
 * AGM-51 (8, I-2526): on any other screen of a guest's (`home` is given only to a guest), a back row
 * whose way back is a route a guest may not open (Views, Work items) goes to their Home, the page
 * Home opens, and says so. Opened cold its Back would have read "‹ Views" and led home anyway.
 */
export function guestLandingPhoneRows(
  rows: IPhoneChromeRows,
  landing: { title: string; path: string } | null,
  home: { label: string; path: string } | null = null,
): IPhoneChromeRows {
  if (rows.backRow === null) return rows;
  if (landing) return { ...rows, backRow: null, pageRoot: landing };
  if (!home || guestMayOpen(rows.backRow.path)) return rows;
  return { ...rows, backRow: { ...rows.backRow, path: home.path, label: home.label } };
}

/** Whether a guest may open `path` in their work area (`isGuestRoute`); any other address is left be. */
function guestMayOpen(path: string): boolean {
  const address = parseAddress(path);
  return address.kind !== 'work-area' || isGuestRoute(address.route);
}
